import React from 'react';
import fullLight from '../assets/jarvis-light.png';
import fullDark from '../assets/jarvis-dark.png';
import markLight from '../assets/ncmb-light.png';
import markDark from '../assets/ncmb-dark.png';
import jarvisLight from '../assets/jarvis-mark-light.png';
import jarvisDark from '../assets/jarvis-mark-dark.png';
import './Logo.css';

/**
 * The NCMB · Jarvis logo.
 *
 * Supplied as two drawings of the same lockup - one for light surfaces, one
 * for dark, where the navy wordmark becomes white. That is a better answer
 * than the white plate this used to sit on in dark mode: a plate is what you
 * do when you only have one drawing, and there are two.
 *
 * Both are in the page and CSS shows one. The obvious alternative, a <picture>
 * with a prefers-color-scheme source, would follow the operating system and
 * ignore the app's own theme switch - and this app has three states, not two:
 * an explicit light choice, an explicit dark choice, and "follow the system",
 * which is the default and stamps nothing on the root element. Only CSS can
 * read all three.
 *
 * Two croppings of each:
 *
 *   `jarvis` - the Jarvis half: the ticket mark and the product name. The
 *              left-hand end of the header, where a logo is also the way home.
 *   `mark`   - the NCMB half. The right-hand end of the header. Split across
 *              the two ends rather than stacked at one, because the full
 *              lockup at header height renders its tagline lines at about
 *              three pixels - and because the product and the firm are two
 *              different things to say.
 *   `full`   - the whole thing, both halves either side of the rule. For the
 *              sign-in pages, where there is room to read it.
 *
 * Neither is redrawn or recoloured; both are cut from the supplied artwork.
 */

/** Below this height the full lockup's tagline stops being legible. */
const FULL_MIN_HEIGHT = 44;

/** One img per theme; the stylesheet decides which is on screen. */
function ThemedLogo({ light, dark, height, className, title, variant }) {
  // max-height rather than height, with width and height both auto.
  //
  // A fixed height plus max-width: 100% is a trap: in a narrow container the
  // width clamps, object-fit letterboxes the drawing inside the box, and the
  // logo is quietly smaller than the height it claims - which is how the
  // sign-in card ended up rendering a 58px logo at about 47. Constraining the
  // height instead lets the browser take whichever limit bites first and keep
  // the aspect ratio exactly.
  const common = { style: { maxHeight: height }, draggable: 'false' };
  return (
    <span className={`ncmb-logo-wrap ${className}`.trim()} style={{ maxHeight: height }}>
      <img src={light} alt={title}
           className={`ncmb-logo is-${variant} for-light`} {...common} />
      {/* Empty alt on the dark one. Both are in the page at all times and only
          one is ever displayed, so naming both would have a screen reader
          announce the logo twice. */}
      <img src={dark} alt=""
           className={`ncmb-logo is-${variant} for-dark`} {...common} />
    </span>
  );
}

export function LogoMark({ height = 30, className = '', title = 'NCMB' }) {
  return (
    <ThemedLogo light={markLight} dark={markDark} height={height}
                className={className} title={title} variant="mark" />
  );
}

/**
 * The full lockup.
 *
 * Refuses to draw below the height its tagline needs and falls back to the
 * mark. A logo squeezed until half of it turns to mush reads as carelessness
 * about the brand, which is the one thing a logo exists to prevent.
 */
export function LogoFull({
  height = 64,
  className = '',
  title = 'NCMB · Jarvis — IT support ticketing'
}) {
  if (height < FULL_MIN_HEIGHT) {
    return <LogoMark height={height} className={className} />;
  }
  return (
    <ThemedLogo light={fullLight} dark={fullDark} height={height}
                className={className} title={title} variant="full" />
  );
}

/**
 * The product mark: the ticket and the Jarvis name.
 *
 * This is what sits at the left-hand end of the header, where a logo doubles
 * as the way back to the start - and what is at the start is the application,
 * not the firm. NCMB goes at the other end.
 */
export function LogoJarvis({ height = 30, className = '', title = 'Jarvis' }) {
  return (
    <ThemedLogo light={jarvisLight} dark={jarvisDark} height={height}
                className={className} title={title} variant="jarvis" />
  );
}

/** What the navbar's home link carries. */
export function Logo({ size = 30 }) {
  return <LogoJarvis height={size} />;
}

/**
 * Stacked lockup for sign-in and other empty canvases.
 *
 * No descriptor underneath any more: the artwork carries "IT SUPPORT
 * TICKETING" under the Jarvis wordmark, and a second subtitle beneath it said
 * the same thing twice.
 */
export function LogoStacked({ size = 64 }) {
  return (
    <div className="logo-stacked">
      <LogoFull height={size} />
    </div>
  );
}

export default Logo;
